/**
 * Statsig Event Constants
 * Based on PE-452 Events Audit requirements
 */

export const STATSIG_EVENTS = {
  // Standard Event Types (KIN-175)
  // Use these as eventName with specific value for consistent analytics
  IMPRESSION: "entrypoint_viewed",
  BUTTON_PRESS: "cta_clicked",

  // Onboarding & Welcome Flow
  ONBOARDING_WELCOME_CONTINUE: "onboarding_welcome_continue",

  // HIPAA & Privacy
  HIPAA_CONSENT_ACCEPTED: "hipaa_consent_accepted",

  // Triage Flow
  TRIAGE_REQUEST_CONFIRMED: "triage_request_confirmed",
  TRIAGE_REQUEST_CANCELLED: "triage_request_cancelled",

  // Visit Flow
  START_A_VISIT: "visit_request_started",
  VISIT_REQUEST_CANCELLED: "visit_request_cancelled",
  VISIT_COMPLETED: "visit_completed",
  VISIT_ACTIVE: "visit_active",

  // Profile & Registration
  PROFILE_SUBMITTED: "profile_submitted",
  PROFILE_CANCELLED: "profile_cancelled",
  PROFILE_COMPLETED: "care_profile_completed",

  // Account Creation & Login
  ACCOUNT_CREATED: "account_created",
  ACCOUNT_EXISTING_RETURN: "account_existing_return",

  // Settings & Subscription
  MANAGE_SUBSCRIPTION: "manage_subscription",
  TERMS_CONDITIONS: "terms_conditions",
  CONTACT_SUPPORT: "contact_support",
  SUB_CANCEL_REACTIVATE: "sub_cancel_reactivate",
  PAYMENT_ERR_MANAGE: "payment_err_manage",

  // Error Handling
  ERROR_SCREEN_SHOWN: "error_shown",

  // MDI Questionnaire Events
  MDI_QUESTIONNAIRE_LOADED: "mdi_questionnaire_loaded",
  MDI_QUESTIONNAIRE_STARTED: "mdi_intake_started",
  MDI_QUESTIONNAIRE_FINISHED: "mdi_intake_completed",
  MDI_QUESTIONNAIRE_UNLOAD: "mdi_questionnaire_unload",
  MDI_QUESTIONNAIRE_STEP: "mdi_questionnaire_step",
  MDI_ENCOUNTER_CREATED: "mdi_encounter_created",
  MDI_PATIENT_CREATED: "mdi_patient_created",
  MDI_QUESTION_SKIPPED: "mdi_question_skipped",
  MDI_DEAD_END_QUESTION: "mdi_dead_end_question",
  MDI_PATIENT_LOGGED_IN: "mdi_patient_logged_in",

  // Link Shortener
  SHORTLINK_FOLLOWED: "shortlink_followed",

  // NPS Survey
  NPS_COMPLETED: "nps_completed",

  // Funnel & Lifecycle Events (PE-874)
  /** Drop-off + abandonment. Values: home, list, paywall, checkout, widget */
  UI_DISMISSED: "ui_dismissed",
  /** Subscription funnel step. Values: two-product-paywall, variant IDs */
  PAYWALL_VIEWED: "paywall_viewed",
  /** Checkout handoff. Values: stripe_hosted, embedded, other */
  CHECKOUT_VIEWED: "checkout_viewed",
  /** Last client intent before server-truth conversion. Values: card, apple_pay, google_pay, unknown */
  PAYMENT_SUBMITTED: "payment_submitted",
  /** Chosen conditions + visit funnel step. Values: condition IDs (normalized) */
  CONDITION_SELECTED: "condition_selected",
  /** Track new subscriptions (Stripe webhook). Values: unlimited_rx, kids_care, other */
  SUBSCRIPTION_CREATED: "subscription_created",
  /** Track subscription state changes (Stripe webhook). Values: trial, active, canceled_but_active, canceled, past_due */
  SUBSCRIPTION_STATUS_UPDATED: "subscription_status_updated",
  /** Track cancellations (Stripe webhook). Values: unlimited_rx, kids_care, other */
  SUBSCRIPTION_CANCELED: "subscription_canceled",
  /** Power ARPS/ARPU revenue metrics and renewal retention. Values: revenue_usd_cents (integer) */
  SUBSCRIPTION_REVENUE_RECOGNIZED: "subscription_revenue_recognized",
  /** Track MDI case creation (server-side). Values: visit, rx_only, message_only, unknown */
  CASE_CREATED: "case_created",
  /** Track clinician assignment */
  VISIT_PROVIDER_ASSIGNED: "visit_provider_assigned",
  /** Track visit cancellation (server-side) */
  VISIT_CANCELLED: "visit_cancelled",
} as const;

export type StatsigEventName =
  (typeof STATSIG_EVENTS)[keyof typeof STATSIG_EVENTS];

/**
 * Allowed values for cta_clicked event
 * Purpose: Capture success + CTA effectiveness
 */
export const CTA_CLICKED_VALUES = {
  START_FREE_TRIAL: "start_free_trial",
  START_VISIT: "start_visit",
  CONTINUE_CHECKOUT: "continue_checkout",
  MANAGE_SUBSCRIPTION: "manage_subscription",
  LEARN_MORE: "learn_more",
  REACTIVATE_SUBSCRIPTION: "reactivate_subscription",
} as const;

export type CtaClickedValue =
  (typeof CTA_CLICKED_VALUES)[keyof typeof CTA_CLICKED_VALUES];

/**
 * Allowed values for entrypoint_viewed event
 * Purpose: Power Overall Conversion and Entry Point Funnels
 */
export const ENTRYPOINT_VIEWED_VALUES = {
  TRIAGE: "triage",
  DISMISSIBLE_CTA: "dismissible_cta",
  FAB: "fab",
  DEEP_LINK: "deep_link",
  PUSH: "push",
  EMAIL: "email",
  UNKNOWN: "unknown",
} as const;

export type EntrypointViewedValue =
  (typeof ENTRYPOINT_VIEWED_VALUES)[keyof typeof ENTRYPOINT_VIEWED_VALUES];

/**
 * Allowed values for error_shown event
 * Purpose: Quantify error rates and correlate with conversion/drop-off
 */
export const ERROR_SHOWN_VALUES = {
  NETWORK: "network",
  PAYMENT: "payment",
  MDI: "mdi",
  SERVER: "server",
  UNKNOWN: "unknown",
} as const;

export type ErrorShownValue =
  (typeof ERROR_SHOWN_VALUES)[keyof typeof ERROR_SHOWN_VALUES];

/**
 * Allowed values for nps_completed event
 * Purpose: Track NPS survey completion
 */
export const NPS_COMPLETED_VALUES = {
  PROMOTER: "promoter",
  PASSIVE: "passive",
  DETRACTOR: "detractor",
} as const;

export type NpsCompletedValue =
  (typeof NPS_COMPLETED_VALUES)[keyof typeof NPS_COMPLETED_VALUES];

/**
 * Allowed values for mdi_intake_started event
 * Purpose: Track MDI intake questionnaire start
 */
export const MDI_INTAKE_STARTED_VALUES = {
  TRIAGE: "triage",
  DIRECT: "direct",
  UNKNOWN: "unknown",
} as const;

export type MdiIntakeStartedValue =
  (typeof MDI_INTAKE_STARTED_VALUES)[keyof typeof MDI_INTAKE_STARTED_VALUES];

/**
 * Allowed values for mdi_intake_completed event
 * Purpose: Track MDI intake questionnaire completion
 */
export const MDI_INTAKE_COMPLETED_VALUES = {
  TRIAGE: "triage",
  DIRECT: "direct",
  UNKNOWN: "unknown",
} as const;

export type MdiIntakeCompletedValue =
  (typeof MDI_INTAKE_COMPLETED_VALUES)[keyof typeof MDI_INTAKE_COMPLETED_VALUES];

/**
 * Allowed values for care_profile_completed event
 * Purpose: Visit funnel milestone (profile done)
 */
export const CARE_PROFILE_COMPLETED_VALUES = {
  ADULT: "adult",
  CHILD: "child",
  UNKNOWN: "unknown",
} as const;

export type CareProfileCompletedValue =
  (typeof CARE_PROFILE_COMPLETED_VALUES)[keyof typeof CARE_PROFILE_COMPLETED_VALUES];

/**
 * Allowed values for visit_request_started event
 * Purpose: Visit request method + visit funnel start
 */
export const VISIT_REQUEST_STARTED_VALUES = {
  TRIAGE: "triage",
  MANUAL: "manual",
  DEEP_LINK: "deep_link",
} as const;

export type VisitRequestStartedValue =
  (typeof VISIT_REQUEST_STARTED_VALUES)[keyof typeof VISIT_REQUEST_STARTED_VALUES];

/**
 * Allowed values for visit_cancelled event
 * Purpose: Track visit cancellation reasons
 */
export const VISIT_CANCELLED_VALUES = {
  PATIENT_REQUEST: "patient_request",
  PROVIDER_UNAVAILABLE: "provider_unavailable",
  DUPLICATE_CASE: "duplicate_case",
  OUT_OF_SCOPE: "out_of_scope",
  TECHNICAL_ERROR: "technical_error",
  TIMEOUT: "timeout",
  OTHER: "other",
} as const;

export type VisitCancelledValue =
  (typeof VISIT_CANCELLED_VALUES)[keyof typeof VISIT_CANCELLED_VALUES];

/**
 * Map MDI cancel reason string to taxonomy-compliant VisitCancelledValue
 */
export function mapMdiCancelReason(mdiReason: string): VisitCancelledValue {
  const normalized = mdiReason.toLowerCase().trim();

  if (normalized.includes("patient") || normalized.includes("user")) {
    return VISIT_CANCELLED_VALUES.PATIENT_REQUEST;
  }
  if (normalized.includes("provider") || normalized.includes("clinician")) {
    return VISIT_CANCELLED_VALUES.PROVIDER_UNAVAILABLE;
  }
  if (normalized.includes("duplicate")) {
    return VISIT_CANCELLED_VALUES.DUPLICATE_CASE;
  }
  if (normalized.includes("scope") || normalized.includes("ineligible")) {
    return VISIT_CANCELLED_VALUES.OUT_OF_SCOPE;
  }
  if (normalized.includes("technical") || normalized.includes("error")) {
    return VISIT_CANCELLED_VALUES.TECHNICAL_ERROR;
  }
  if (normalized.includes("timeout") || normalized.includes("expired")) {
    return VISIT_CANCELLED_VALUES.TIMEOUT;
  }

  return VISIT_CANCELLED_VALUES.OTHER;
}

/**
 * Utility function to create a consistent Statsig event object
 */
export function createStatsigEvent(
  eventName: StatsigEventName,
  metadata: Record<string, any> = {}
) {
  return {
    eventName,
    metadata,
  };
}
